import "@progress/kendo-ui/js/kendo.scheduler";
import * as ko from "knockout";
import * as moment from "moment";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import * as appointmentCalendarData from "../data/appointmentCalendarData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";
import { ICustomerSettings } from "../interfaces/user";

interface IInspectorObservable {
    ID: KnockoutObservable<string>;
    Name: KnockoutObservable<string>;
    IsChecked: KnockoutObservable<boolean>;
}

interface ITaskCalendarViewModel {
    Inspectors: KnockoutObservableArray<IInspectorObservable>;
    StartTime: KnockoutObservable<string>;
    EndTime: KnockoutObservable<string>;
    IsInspector: KnockoutObservable<boolean>;
    IsUserManager: KnockoutObservable<boolean>;
    InspectorsLabel: string;
    AppointmentLabel: string;
    AppointmentSummaryInfo: KnockoutObservable<appointmentCalendarData.IAppointmentSummaryInfo>;
    selectAll: () => void;
    deselectAll: () => void;
    onSubmit: () => void;
    saveDefaultInspectors: () => void;
    openInspectors: () => void;
    refreshCalendar: () => void;
    closeInspectorModal: () => void;
    closeAppointmentSummaryModal: () => void;
}

// constants
const UPDATE_CALENDAR: string = "Update Calendar";
const SAVE_AS_DEFAULT: string = "Save as Default";

const schema: any = {
    model: {
        id: "taskId",
        fields: {
            taskId: { from: "TaskID", type: "number" },
            title: { from: "Title", defaultValue: "No title", validation: { required: true } },
            start: { type: "date", from: "Start" },
            end: { type: "date", from: "End" },
            startTimezone: { from: "StartTimezone" },
            endTimezone: { from: "EndTimezone" },
            description: { from: "Description" },
            recurrenceId: { from: "RecurrenceID" },
            recurrenceRule: { from: "RecurrenceRule" },
            recurrenceException: { from: "RecurrenceException" },
            ownerId: { from: "OwnerID", defaultValue: 1 },
            isAllDay: { type: "boolean", from: "IsAllDay" },
            reschedule: { from: "Reschedule" },
            isEditable: { type: "boolean", from: "IsEditable" },
            inspectorID: { from: "InspectorID" },
            appointmentStatus: { from: "AppointmentStatus" },
            appointmentShortName: { from: "AppointmentShortName" },
            color: { from: "Color" },
        },
    },
};

// -------------------------------------------------------------------------------
export class TaskCalendarController implements IController {
    // references to DOM elements

    private container: JQuery;
    private viewModel: ITaskCalendarViewModel;
    private inspectorModal: JQuery;
    private appointmentSummaryModal: JQuery;
    private scheduler: kendo.ui.Scheduler;
    private currentViewName = "day";
    private currentDate = new Date();
    private resources: appointmentCalendarData.ISchedulerResourcesViewModel[];
    private majorTick: number = 10;
    private resizeTimeoutID: number;
    private originalInspectors: appointmentCalendarData.IInspector[];
    private customerSettings: ICustomerSettings[];

    constructor() {
        const me: TaskCalendarController = this;

        this.viewModel = {
            Inspectors: ko.observableArray<IInspectorObservable>(),
            StartTime: ko.observable(""),
            EndTime: ko.observable(""),
            // Views: ko.observableArray(),
            IsInspector: ko.observable<boolean>(false),
            IsUserManager: ko.observable(false),
            AppointmentSummaryInfo: ko.observable(),
            selectAll(): void {
                me.selectUnselectAll(true);
            },
            deselectAll(): void {
                me.selectUnselectAll(false);
            },
            onSubmit(): void {
                me.inspectorModal.modal("hide");
                me.saveInspectorsFilter(UPDATE_CALENDAR);

            },
            saveDefaultInspectors(): void {
                me.inspectorModal.modal("hide");
                me.saveInspectorsFilter(SAVE_AS_DEFAULT);
            },
            openInspectors(): void {
                me.originalInspectors = [];
                for (const inspectorObservable of me.viewModel.Inspectors()) {
                    me.originalInspectors.push({
                        ID: inspectorObservable.ID(),
                        IsChecked: inspectorObservable.IsChecked(),
                        Name: inspectorObservable.Name(),
                    });
                }
                me.inspectorModal.modal("show");
            },
            refreshCalendar(): void {
                me.refresh();
            },
            closeInspectorModal: () => {
                me.viewModel.Inspectors.removeAll();
                for (const inspector of me.originalInspectors) {
                    me.viewModel.Inspectors.push({
                        ID: ko.observable(inspector.ID),
                        IsChecked: ko.observable(inspector.IsChecked),
                        Name: ko.observable(inspector.Name),
                    });
                }
                me.inspectorModal.modal("hide");
            },
            closeAppointmentSummaryModal: () => {
                this.appointmentSummaryModal.modal("hide");
            },
            InspectorsLabel: Constants.LABEL_INSPECTORS,
            AppointmentLabel: Constants.LABEL_APPOINTMENT,
        };
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        this.container = container;

        this.inspectorModal = container.find("div[data-id='inspectorModal']");
        this.appointmentSummaryModal = container.find("div[data-id='appointmentSummaryModal']");
        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(): JQueryPromise<any> {
        const me: TaskCalendarController = this;
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const app = Global.app();
        waitContainer = UI.showWaitIndicator(me.container);

        appointmentCalendarData.loadSettings()
            .done((response: Data.IResponse<appointmentCalendarData.ISchedulerSettingsModel>) => {

                let startDate: string;
                let startDateObject: moment.Moment;
                const urlDate: string = Util.getURLParameter("date");
                if (Util.isNullOrEmpty(urlDate)) {
                    startDateObject = moment();
                } else {
                    startDateObject = moment(urlDate);
                }

                me.currentDate = startDateObject.toDate();
                startDate = startDateObject.format("MM-DD-YYYY");

                me.updateDefaultInspectors(response.Payload.Inspectors);

                me.viewModel.StartTime(startDate + " " + response.Payload.StartTimeString);
                me.viewModel.EndTime(startDate + " " + response.Payload.EndTimeString);
                me.viewModel.IsInspector(response.Payload.IsInspector);
                me.viewModel.IsUserManager(app.userSession ? app.userSession.Permissions.indexOf(Constants.PERMISSION_VIEW_MOBILE_USERS) >= 0 : false);
                me.resources = response.Payload.Resources;
                me.majorTick = response.Payload.MajorTick;

                me.buildControl();

                deferred.resolve();
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    // called when user switches to this page after it has been loaded
    public refresh(): JQueryPromise<any> {

        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        let urlDate: string;
        let startDateObject: moment.Moment;

        // if a date is passed in the URL, change the current date of the calendar
        urlDate = Util.getURLParameter("date");
        if (!Util.isNullOrEmpty(urlDate)) {
            // use date passed in the URL
            startDateObject = moment(urlDate);
            this.currentDate = startDateObject.toDate();
            this.scheduler.date(this.currentDate);
        }

        this.schedulerRefresh();

        deferred.resolve();
        return deferred.promise();
    }

    public add(): void {
        Global.app().router().navigate("/TaskAdd", true);
    }

    public saveInspectorsFilter(action: string): any {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const me: TaskCalendarController = this;
        const inspectorsFilter: appointmentCalendarData.IInspector[] = [];
        const waitContainer: JQuery = UI.showWaitIndicator(me.container);

        me.viewModel.Inspectors().forEach((inspector: IInspectorObservable) => {
            const record: appointmentCalendarData.IInspector = {
                ID: inspector.ID(),
                Name: inspector.Name(),
                IsChecked: inspector.IsChecked(),
            };

            inspectorsFilter.push(record);
        });

        if (action === SAVE_AS_DEFAULT) {
            appointmentCalendarData.saveInspectors(inspectorsFilter)
                .done((response: Data.IResponse<boolean>) => {
                    if (response.Success) {
                        // save and refresh calendar
                        me.updateResources();
                        me.buildControl();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        }
    }

    public saveDragAndDrop(action: string, scheduledEvent: appointmentCalendarData.IScheduleModel): void {
        const me: TaskCalendarController = this;
        const waitContainer: JQuery = UI.showWaitIndicator(me.container);
        appointmentCalendarData
            .save(action, scheduledEvent)
            .done((response: Data.IResponse<appointmentCalendarData.IScheduleModel[]>) => {
                if (response.Success) {
                    me.schedulerRefresh();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
                me.schedulerRefresh();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });
    }

    public onSchedulerAdd(e: any): void {
        const app: IApplication = Global.app();
        const eStartString: string = moment(e.event.start).format("YYYY-MM-DDTHH:mm:ss.sssZ");

        e.preventDefault();
        app.router().navigate("/TaskAdd?date=" + eStartString + "&inspectorID=" + e.event.inspectorID, true);
    }

    public onSchedulerEdit(e: any): void {
        const app: IApplication = Global.app();
        const event: any = e.event;
        e.preventDefault();
        if (event.taskId != null && event.taskId > 0) {
            app.pageManager().gotoTaskPage(event.taskId, false);
        }
    }

    public onSchedulerMoveStart(e: any): void {
        const event: any = e.event;

        if (!event.isEditable) {
            e.preventDefault();
        }
    }

    public onSchedulerMoveEnd(e: any): void {
        const me: TaskCalendarController = this;
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);
        const action: string = "save";
        const eStartString: string = moment(e.start).format("MM/DD/YYYY hh:mm A");
        const eEndString: string = moment(e.end).format("MM/DD/YYYY hh:mm A");

        if (me.isOverlapEvent(e.event, e.start, e.end, e.resources.inspectorID)) {
            UI.showConfirmDialog(
                "This time slot is occupied with another schedule. Do you want to proceed?",
                () => { me.reschedule(e.event, eStartString, eEndString, e.resources.inspectorID, action); },
                () => { me.schedulerRefresh(); });
        } else {
            me.reschedule(e.event, eStartString, eEndString, e.resources.inspectorID, action);
        }

        UI.hideWaitIndicator(me.container, waitContainer);
    }

    public onSchedulerResizeStart(e: any): void {
        const event: any = e.event; // list of selected Scheduler events

        if (event.isEditable === false) {
            e.preventDefault();
        }
    }

    public onSchedulerResizeEnd(e: any): void {
        const me: TaskCalendarController = this;
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);
        const action: string = "save";
        const eStartString: string = moment(e.start).format("MM/DD/YYYY hh:mm A");
        const eEndString: string = moment(e.end).format("MM/DD/YYYY hh:mm A");

        if (me.isOverlapEvent(e.event, e.start, e.end, e.event.inspectorID)) {
            UI.showConfirmDialog(
                "This time slot is occupied with another schedule. Do you want to proceed?",
                () => { me.reschedule(e.event, eStartString, eEndString, e.event.inspectorID, action); },
                () => { me.schedulerRefresh(); });
        } else {
            me.reschedule(e.event, eStartString, eEndString, e.event.inspectorID, action);
        }

        UI.hideWaitIndicator(me.container, waitContainer);
    }

    // called when the user changes a view or date
    public onNavigate(e: any): void {
        this.schedulerRefresh(e.date, e.view);
        this.currentViewName = e.view;
        this.currentDate = e.date;
    }

    // loads data into the calendar for the specified date and view
    public loadScheduleData(startDate: string, actionView: string): JQuery.Promise<any, any, any> {

        const me: TaskCalendarController = this;
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const inspectors: appointmentCalendarData.IInspector[] = [];

        me.viewModel.Inspectors().forEach((inspectorObservable: IInspectorObservable) => {
            const inspector: appointmentCalendarData.IInspector = {
                ID: inspectorObservable.ID(),
                IsChecked: inspectorObservable.IsChecked(),
                Name: inspectorObservable.Name(),
            };

            inspectors.push(inspector);
        });

        appointmentCalendarData
            .loadAppointments(startDate, actionView, inspectors)
            .done((response: Data.IResponse<appointmentCalendarData.IScheduleModel[]>) => {
                if (response.Success) {

                    me.scheduler.setDataSource(new kendo.data.SchedulerDataSource({
                        data: response.Payload,
                        schema,
                    }));

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
                deferred.resolve();
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    public updateDefaultInspectors(inspectors: appointmentCalendarData.IInspector[]): void {
        const me: TaskCalendarController = this;

        inspectors.forEach((inspector: appointmentCalendarData.IInspector) => {
            const koInspector: IInspectorObservable = {
                ID: ko.observable(inspector.ID),
                Name: ko.observable(inspector.Name),
                IsChecked: ko.observable(inspector.IsChecked),
            };

            me.viewModel.Inspectors.push(koInspector);
        });
    }

    // reload the calendar using the current view and date
    public schedulerRefresh(schedulerDate?: Date, schedulerView?: string): void {

        const date: Date = schedulerDate ? schedulerDate : this.scheduler.date();
        const view: string = schedulerView ? schedulerView : this.scheduler.viewName();

        this.loadScheduleData(moment(date).format("MM-DD-YYYY"), view);
    }

    public selectUnselectAll(select: boolean): void {
        const me: TaskCalendarController = this;

        me.viewModel.Inspectors().forEach((inspector: IInspectorObservable) => {
            inspector.IsChecked(select);
        });
    }

    public loadRecord(id: number): JQueryPromise<any> {
        // do nothing, not implemented for this control
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        deferred.resolve();
        return deferred.promise();
    }

    // update the height of the scrollable area
    public height(height: number): void {
        const fieldHeight: number = height - 110;
        const me: TaskCalendarController = this;

        if (fieldHeight > 0) {

            if (this.scheduler != null) {
                clearTimeout(this.resizeTimeoutID);
                this.resizeTimeoutID = window.setTimeout(() => {
                    // size widget to take the whole view
                    me.scheduler.element.height(fieldHeight);
                    me.scheduler.resize(true);
                }, 500);
            }

            this.container.find("div[data-id='scheduler']").height(fieldHeight);
            this.inspectorModal.css("max-height", fieldHeight);
        }
    }
    public doValidation(): boolean {
        return true;
    }

    public haveFieldsChanged(): boolean {
        // no data is loaded
        return false;
    }

    // returns a title for the page
    public title(): string {
        return `${Constants.LABEL_APPOINTMENT} Calendar`;
    }

    public subTitle(): string {
        return "";
    }

    public allowRecordToChangeAfterLoad(): boolean {
        return false;
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "AppointmentCalendar";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    private isOverlapEvent(event: any, newStart: any, newEnd: any, newInspectorID: any): boolean {
        const me: TaskCalendarController = this;
        let isOverlap: boolean = false;
        const eventList: any[] = me.scheduler.occurrencesInRange(newStart, newEnd);
        if (eventList != null && eventList.length > 0) {
            if (eventList.filter((x) => x.taskId !== event.taskId && x.inspectorID === newInspectorID).length >= 1) {  // exclude itself
                isOverlap = true;
            }
        }
        return isOverlap;
    }

    private reschedule(event: any, slotStartString: string, slotEndString: string, inspectorID: string, action: string): void {
        const me: TaskCalendarController = this;
        const newEvent: appointmentCalendarData.IScheduleModel = me.assignEvent(event, inspectorID, slotStartString, slotEndString);
        me.saveDragAndDrop(action, newEvent);
    }

    private assignEvent(event: any, inspectorID: string, start: any, end: any): appointmentCalendarData.IScheduleModel {
        let newScheduleEvent: appointmentCalendarData.IScheduleModel;

        newScheduleEvent = {
            TaskID: event.taskId,
            InspectorID: inspectorID,
            Title: event.title,
            Description: event.description,
            StartTimezone: event.startTimezone,
            Start: start,
            End: end,
            EndTimezone: event.endTimezone,
            RecurrenceRule: event.recurrenceRule,
            RecurrenceID: event.recurrenceId,
            RecurrenceException: event.recurrenceException,
            IsAllDay: event.isAllDay,
            Reschedule: event.reschedule,
            IsEditable: event.isEditable,
            AppointmentStatus: event.appointmentStatus,
            AppointmentShortName: event.appointmentShortName,
            Color: event.color,
        };
        return newScheduleEvent;
    }

    private updateResources(): void {
        const me: TaskCalendarController = this;

        me.resources = [];

        me.viewModel.Inspectors().forEach((inspector: IInspectorObservable) => {
            if (inspector.IsChecked()) {
                me.resources.push({ text: inspector.Name(), value: inspector.ID(), color: "#0099FF" });
            }
        });
    }

    private buildControl(): void {
        const app: IApplication = Global.app();
        const me: TaskCalendarController = this;

        // Need CustomerSettings to get the Selected format
        me.customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        const  customerSelectedTimeFormat =  UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT);

        let startDateTime: moment.Moment;
        let endDateTime: moment.Moment;
        const schedulerContainer: JQuery<HTMLElement> = me.container.find("div[data-id='scheduler']");
        let defaultViews: kendo.ui.SchedulerView[];

        // generate calender
        startDateTime = moment(me.viewModel.StartTime(), Constants.MOMENT_FORMAT_DATETIME_FROM_CONFIG);
        endDateTime = moment(me.viewModel.EndTime(), Constants.MOMENT_FORMAT_DATETIME_FROM_CONFIG);

        // detaches all event handlers
        if (me.scheduler) { me.scheduler.destroy(); }

        // remove any existing control
        schedulerContainer.empty();

        // create the control
        defaultViews = [
            { title: "Day", type: "kendo.ui.DayView", showWorkHours: true, selected: (me.currentViewName.toLowerCase().indexOf("day") >= 0) },
            { title: "Month", type: "kendo.ui.MonthView", selected: (me.currentViewName.toLowerCase().indexOf("month") >= 0) },
        ];

        me.scheduler = schedulerContainer.kendoScheduler({
            moveStart: me.onSchedulerMoveStart.bind(me),
            moveEnd: me.onSchedulerMoveEnd.bind(me),
            resizeStart: me.onSchedulerResizeStart.bind(me),
            resizeEnd: me.onSchedulerResizeEnd.bind(me),
            edit: me.onSchedulerEdit.bind(me),
            add: me.onSchedulerAdd.bind(me),
            navigate: me.onNavigate.bind(me),
            date: me.currentDate,
            workDayStart: startDateTime.toDate(),
            workDayEnd: endDateTime.toDate(),
            views: defaultViews,
            majorTimeHeaderTemplate: kendo.template(`#=kendo.toString(date,"${UI.getKendoTimeFormat(customerSelectedTimeFormat)}")#`),
            majorTick: me.majorTick,
            minorTickCount: 2, // two time slots per major tick
            selectable: true,
            allDaySlot: false,
            snap: false,
            eventTemplate: kendo.template($("#template_appointmentcalendar_detail").html()),
            group: {
                resources: ["Inspectors"],
            },
            resources: [
                {
                    field: "inspectorID",
                    name: "Inspectors",
                    dataSource: me.resources,
                    title: "Name",
                },
            ],
            editable: {
                destroy: false,
                create: true,
                move: true,
                resize: true,
            },
            height: 300,
            dataBound: me.onDataBind.bind(me),
        }).data("kendoScheduler");

        me.height(app.pageManager().workspaceHeight());

        me.schedulerRefresh(this.currentDate, this.currentViewName);
    }

    private onDataBind(e: any): void {
        const rows: JQuery<HTMLElement> = $(".k-scheduler-layout tr:first .k-scheduler-table")
            .not(".k-scheduler-header-all-day")
            .find("tr:eq(1)");
        // remove date row when in "day" view
        if (e.sender.view().name === "day") {
            rows.hide();
        }

        // wire up the event info buttons
        $(".calendar-info-link").click(this.onEventInfoClick.bind(this));
    }

    private onEventInfoClick(e: any) {

        this.viewModel.AppointmentSummaryInfo(null);

        const taskID: string = $(e.currentTarget).attr("data-id")!;

        const waitContainer = UI.showWaitIndicator(this.container);

        appointmentCalendarData.getAppointmentSummary(taskID)
            .then((response) => {
                if (response.Success) {
                    this.viewModel.AppointmentSummaryInfo(response.Payload);
                    this.appointmentSummaryModal.modal("show");
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            }).always(() => {
                UI.hideWaitIndicator(this.container, waitContainer);
            });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/taskCalendar.ts